import React, { useState, useEffect, useCallback } from "react";
import { Shield, Building, Mail, CheckCircle2, AlertCircle, Plus, User, X, BarChart3, HardDrive, MessageSquare, RefreshCw, Loader2, Users, Flag, Cpu } from "lucide-react";
import "./DatabaseSetupPage.css"; // Reuse existing card styles

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");

// Retry transient network failures (e.g. Lambda cold-start blips) with backoff, so the
// admin page doesn't get stuck on a skeleton after a deploy/cold start.
async function resilientFetch(url, options = {}, maxRetries = 2) {
  let lastErr;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetch(url, options);
    } catch (err) {
      lastErr = err;
      if (attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
        continue;
      }
    }
  }
  throw lastErr;
}

export default function AdminPage({ idToken, onClose }) {
  const [view, setView] = useState("onboard");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [role, setRole] = useState("user");
  const [creditLimit, setCreditLimit] = useState("100");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // Reset user access (resend invite / password reset)
  const [resetEmail, setResetEmail] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState(null);
  const [resetErr, setResetErr] = useState(null);

  // Usage overview
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState(null);
  const [trackingEnabled, setTrackingEnabled] = useState(null);
  const [togglingTracking, setTogglingTracking] = useState(false);
  const [backendVersion, setBackendVersion] = useState(null);  // deployed API/Lambda version
  const [insights, setInsights] = useState(null);
  const [health, setHealth] = useState(null);  // catalog↔S3 consistency check

  // Feature flags
  const [flags, setFlags] = useState(null);
  const [flagTenants, setFlagTenants] = useState([]);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [flagBusy, setFlagBusy] = useState("");  // "<scope>:<key>:<tenant>" while saving

  // Bedrock model switch (#67)
  const [models, setModels] = useState(null);          // { defaults:{smart,fast}, tenantOverrides:[...] }
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelBusy, setModelBusy] = useState(false);
  const [trialCustomer, setTrialCustomer] = useState("testuser_customer");
  const [trialTier, setTrialTier] = useState("smart");
  const [trialModelId, setTrialModelId] = useState("");
  const [defaultSel, setDefaultSel] = useState({ smart: "", fast: "" }); // dropdown selection per tier
  const [modelError, setModelError] = useState("");   // surfaced when a save is rejected (e.g. model not enabled)

  const fetchFlags = useCallback(async () => {
    setFlagsLoading(true);
    try {
      const res = await resilientFetch(`${API_URL}/admin/feature-flags`, { headers: { Authorization: `Bearer ${idToken}` } });
      if (res.ok) { const d = await res.json(); setFlags(d.flags || []); setFlagTenants(d.tenants || []); }
    } catch (e) { /* ignore */ } finally { setFlagsLoading(false); }
  }, [idToken]);

  const setGlobalFlag = async (flagKey, enabled) => {
    setFlagBusy(`global:${flagKey}:`);
    try {
      const res = await fetch(`${API_URL}/admin/feature-flags/global`, {
        method: "POST", headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ flag_key: flagKey, enabled }),
      });
      if (res.ok) await fetchFlags();
    } catch (e) { /* ignore */ } finally { setFlagBusy(""); }
  };

  const setTenantFlag = async (flagKey, customerId, enabled) => {
    setFlagBusy(`tenant:${flagKey}:${customerId}`);
    try {
      const res = await fetch(`${API_URL}/admin/feature-flags/tenant`, {
        method: "POST", headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ flag_key: flagKey, customer_id: customerId, enabled }),
      });
      if (res.ok) await fetchFlags();
    } catch (e) { /* ignore */ } finally { setFlagBusy(""); }
  };

  const fetchModels = useCallback(async () => {
    setModelsLoading(true);
    try {
      const res = await resilientFetch(`${API_URL}/admin/models`, { headers: { Authorization: `Bearer ${idToken}` } });
      if (res.ok) {
        const data = await res.json();
        setModels(data);
        setDefaultSel({ smart: data?.defaults?.smart || "", fast: data?.defaults?.fast || "" });
      }
    } catch (e) { /* ignore */ } finally { setModelsLoading(false); }
  }, [idToken]);

  // Switch the GLOBAL default model for a tier (all tenants, no redeploy). model_id=null resets to the build default.
  const setModelDefault = async (tier, modelId) => {
    setModelBusy(true); setModelError("");
    try {
      const res = await fetch(`${API_URL}/admin/models/default`, {
        method: "POST", headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ tier, model_id: modelId }),
      });
      if (res.ok) { await fetchModels(); return true; }
      const body = await res.json().catch(() => ({}));
      setModelError(body?.detail || `Could not set ${tier} model (HTTP ${res.status}).`);
    } catch (e) { setModelError("Network error setting the model. Try again."); } finally { setModelBusy(false); }
    return false;
  };

  const setModelOverride = async (customerId, tier, modelId) => {
    setModelBusy(true); setModelError("");
    try {
      const res = await fetch(`${API_URL}/admin/models/tenant`, {
        method: "POST", headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customerId, tier, model_id: modelId }),
      });
      if (res.ok) { await fetchModels(); return true; }
      const body = await res.json().catch(() => ({}));
      setModelError(body?.detail || `Could not set the trial model (HTTP ${res.status}).`);
    } catch (e) { setModelError("Network error setting the trial model. Try again."); } finally { setModelBusy(false); }
    return false;
  };

  const fetchUsage = useCallback(async (silent = false) => {
    if (!silent) setUsageLoading(true);
    setUsageError(null);
    try {
      const res = await resilientFetch(`${API_URL}/admin/usage`, { headers: { Authorization: `Bearer ${idToken}` } });
      if (!res.ok) throw new Error("Could not load usage data.");
      setUsage(await res.json());
    } catch (e) {
      setUsageError("Couldn't load usage data (the server may have been waking up). Tap Refresh to try again.");
    } finally {
      if (!silent) setUsageLoading(false);
    }
  }, [idToken]);

  // Auto-refresh the Per-customer usage panel every 30s while it's open (silent — no skeleton
  // flicker), and immediately when the tab regains focus, so status / files / questions stay
  // current without a manual Refresh. Presence (Active/Idle/Offline) is time-based on the client,
  // so it also re-evaluates on each refresh.
  useEffect(() => {
    if (view !== "usage" || !idToken) return undefined;
    const tick = () => { if (document.visibilityState === "visible") fetchUsage(true); };
    const id = setInterval(tick, 30000);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, [view, idToken, fetchUsage]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await resilientFetch(`${API_URL}/admin/settings`, { headers: { Authorization: `Bearer ${idToken}` } });
      if (res.ok) { const d = await res.json(); setTrackingEnabled(!!d.usageTrackingEnabled); setBackendVersion(d.backendVersion || null); }
    } catch (e) { /* ignore */ }
  }, [idToken]);

  const toggleTracking = async () => {
    setTogglingTracking(true);
    try {
      const next = !trackingEnabled;
      const res = await fetch(`${API_URL}/admin/settings/usage-tracking`, {
        method: "POST", headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      if (res.ok) { const d = await res.json(); setTrackingEnabled(!!d.usageTrackingEnabled); }
    } catch (e) { /* ignore */ } finally { setTogglingTracking(false); }
  };

  const fetchInsights = useCallback(async () => {
    try {
      const res = await resilientFetch(`${API_URL}/admin/usage-insights`, { headers: { Authorization: `Bearer ${idToken}` } });
      if (res.ok) setInsights(await res.json());
    } catch (e) { /* ignore */ }
  }, [idToken]);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await resilientFetch(`${API_URL}/admin/data-health`, { headers: { Authorization: `Bearer ${idToken}` } });
      if (res.ok) setHealth(await res.json());
    } catch (e) { /* ignore */ }
  }, [idToken]);

  useEffect(() => {
    if (view === "usage") {
      if (!usage && !usageLoading) fetchUsage();
      if (trackingEnabled === null) fetchSettings();
      if (insights === null) fetchInsights();
      if (health === null) fetchHealth();
    }
    if (view === "features" && flags === null && !flagsLoading) {
      fetchFlags();
    }
    if (view === "models" && models === null && !modelsLoading) {
      fetchModels();
    }
  }, [view, usage, usageLoading, fetchUsage, trackingEnabled, fetchSettings, insights, fetchInsights, health, fetchHealth, flags, flagsLoading, fetchFlags, models, modelsLoading, fetchModels]);

  const handleResetAccess = async (e) => {
    e.preventDefault();
    setResetMsg(null); setResetErr(null);
    const target = resetEmail.trim();
    if (!target || !target.includes("@")) return setResetErr("Enter a valid email.");
    setResetBusy(true);
    try {
      const res = await fetch(`${API_URL}/admin/users/reset-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ email: target }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not reset access for this user.");
      setResetMsg(data.message || "Done.");
      setResetEmail("");
    } catch (err) {
      setResetErr(err.message || "Something went wrong.");
    } finally {
      setResetBusy(false);
    }
  };

  const handleOnboard = async (e) => {
    e.preventDefault();
    if (!email || !companyName) return setError("Both fields are required.");
    
    setLoading(true); setError(null); setSuccess(null);
    
    try {
      const res = await fetch(`${API_URL}/admin/onboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({
          email,
          company_name: companyName,
          tenant_type: "LightUser",
          role,
          max_monthly_credits: creditLimit ? parseFloat(creditLimit) : undefined,
        })
      });
      
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to onboard customer.");
      }
      
      const data = await res.json();
      setSuccess(`Onboarded "${data.customer_id}" as ${data.role === "admin" ? "an Admin" : "a User"} with a $${data.monthly_credit_limit_usd}/mo AI credit limit. An invitation email with a temporary password was sent to ${data.invite_email_sent_to}.`);
      setEmail(""); setCompanyName(""); setCreditLimit("100"); setRole("user");
      // Auto-close back to the dashboard after the confirmation is shown.
      if (onClose) setTimeout(() => onClose(), 3500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '11px 14px 11px 38px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none', background: '#fff', boxShadow: '0 1px 2px rgba(16,24,40,.04)', transition: 'border-color .15s, box-shadow .15s' };
  const onFocus = (e) => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,.12)'; };
  const onBlur = (e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = '0 1px 2px rgba(16,24,40,.04)'; };
  const timeAgo = (iso) => {
    if (!iso) return '—';
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  // Real-time presence derived from how recently the customer was active (lastActiveAt),
  // NOT the account enabled/disabled flag. The app sends a heartbeat ping every ~2 min
  // while a tab is open, so an online user stays "Active" (≤5m). A disabled account
  // (isActive === false) is shown distinctly regardless of recency.
  const presence = (iso, isActive) => {
    if (isActive === false) return { label: 'Disabled', color: '#dc2626', dot: '#dc2626' };
    if (!iso) return { label: 'Offline', color: '#94a3b8', dot: '#cbd5e1' };
    const m = (Date.now() - new Date(iso).getTime()) / 60000;
    if (m <= 5) return { label: 'Active', color: '#16a34a', dot: '#16a34a' };
    if (m <= 60) return { label: 'Idle', color: '#d97706', dot: '#f59e0b' };
    return { label: 'Offline', color: '#94a3b8', dot: '#cbd5e1' };
  };

  return (
    <div style={{ padding: '8px', maxWidth: (view === 'usage' || view === 'features' || view === 'models') ? '1040px' : '760px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #eaeef4', paddingBottom: '18px', marginBottom: '20px' }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, #6366f1, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 16px rgba(99,102,241,.28)' }}>
          <Shield size={22} color="#fff" />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', color: '#0f172a', letterSpacing: '-.4px' }}>Admin Settings</h1>
          <p style={{ margin: '3px 0 0', color: '#64748b', fontSize: '13.5px' }}>Tenant management, provisioning &amp; usage</p>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        {[{ id: 'onboard', label: 'Onboard', icon: <Plus size={15} /> }, { id: 'usage', label: 'Usage', icon: <BarChart3 size={15} /> }, { id: 'features', label: 'Feature Flags', icon: <Flag size={15} /> }, { id: 'models', label: 'Models', icon: <Cpu size={15} /> }].map(t => (
          <button key={t.id} onClick={() => setView(t.id)} style={{
            display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 16px', borderRadius: '999px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
            border: view === t.id ? '1px solid #6366f1' : '1px solid #e2e8f0',
            background: view === t.id ? '#eef2ff' : '#fff', color: view === t.id ? '#4f46e5' : '#64748b',
          }}>{t.icon} {t.label}</button>
        ))}
      </div>

      {view === 'onboard' && (
      <>
      <div style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06)' }} className="animate-fade">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
          <Building size={18} color="#6366f1" />
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Onboard New Customer</h2>
        </div>

        <p style={{ color: '#475569', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
          Provision a completely isolated environment for a new <strong>Light</strong> customer. This automatically creates their secure <strong>Cognito user</strong> (and emails them a temporary password), sets up their <strong>tenant group</strong> for data isolation, reserves their private <strong>S3 data space</strong>, and registers them in <strong>DynamoDB</strong> with the AI credit limit below.
        </p>

        {error && <div style={{ padding: '12px', background: '#fef2f2', color: '#991b1b', borderRadius: '10px', marginBottom: '20px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px' }}><AlertCircle size={16} /> {error}</div>}
        {success && <div style={{ padding: '12px', background: '#ecfdf5', color: '#0f766e', borderRadius: '10px', marginBottom: '20px', border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px' }}><CheckCircle2 size={16} /> {success}</div>}

        <form onSubmit={handleOnboard} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Company Name</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Building size={16} style={{ position: 'absolute', left: '13px', color: '#94a3b8', zIndex: 1 }} />
              <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} onFocus={onFocus} onBlur={onBlur} placeholder="e.g. Globex Corporation" style={inputStyle} />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Admin Email Address</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Mail size={16} style={{ position: 'absolute', left: '13px', color: '#94a3b8', zIndex: 1 }} />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onFocus={onFocus} onBlur={onBlur} placeholder="admin@globex.com" style={inputStyle} />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Role</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" onClick={() => setRole("user")} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '11px', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', border: role === 'user' ? '1px solid #6366f1' : '1px solid #e2e8f0', background: role === 'user' ? '#eef2ff' : '#fff', color: role === 'user' ? '#4f46e5' : '#64748b' }}>
                <User size={16} /> User
              </button>
              <button type="button" onClick={() => setRole("admin")} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '11px', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', border: role === 'admin' ? '1px solid #6366f1' : '1px solid #e2e8f0', background: role === 'admin' ? '#eef2ff' : '#fff', color: role === 'admin' ? '#4f46e5' : '#64748b' }}>
                <Shield size={16} /> Admin
              </button>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#94a3b8' }}>
              {role === 'admin'
                ? 'Admins can access this Admin panel to onboard customers and review issues.'
                : 'Standard customer access: upload data and ask AI questions within their own tenant.'}
            </p>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Monthly AI Credit Limit (USD)</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: '14px', color: '#94a3b8', fontSize: '14px', fontWeight: 600, zIndex: 1 }}>$</span>
              <input type="number" min="0" step="10" value={creditLimit} onChange={(e) => setCreditLimit(e.target.value)} onFocus={onFocus} onBlur={onBlur} placeholder="100" style={{ ...inputStyle, padding: '11px 14px 11px 30px' }} />
            </div>
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#94a3b8' }}>Caps the customer's Bedrock AI spend per month. They can keep using uploads and viewing data after the cap; only new AI questions pause until the next month.</p>
          </div>

          <div style={{ marginTop: '10px', paddingTop: '20px', borderTop: '1px solid #eaeef4', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button type="button" onClick={() => onClose && onClose()} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 20px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>
              <X size={16} /> {success ? "Close" : "Cancel"}
            </button>
            <button type="submit" disabled={loading || !email || !companyName} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: loading || !email || !companyName ? '#cbd5e1' : 'linear-gradient(135deg, #6366f1, #7c3aed)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: loading || !email || !companyName ? 'not-allowed' : 'pointer', boxShadow: loading || !email || !companyName ? 'none' : '0 6px 16px rgba(99,102,241,.28)' }}>
              {loading ? <span className="btn-spinner animate-spin" style={{ borderTopColor: 'white' }} /> : <Plus size={16} />}
              {loading ? "Onboarding Customer..." : "Onboard Customer"}
            </button>
          </div>
        </form>
      </div>

      {/* Reset user access — resend invite (never-logged-in) or password reset (existing user) */}
      <div style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '14px', padding: '24px', marginTop: '20px', boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06)' }} className="animate-fade">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Shield size={18} color="#6366f1" />
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Reset User Access</h2>
        </div>
        <p style={{ color: '#475569', fontSize: '14px', marginBottom: '18px', lineHeight: '1.6' }}>
          Re-establish login for an existing user. Boomerang picks the right action automatically: if they were invited but never signed in, it <strong>re-sends the invitation</strong> with a fresh temporary password; if they already set a password and forgot it, it <strong>starts a password reset</strong> and emails them a verification code.
        </p>

        {resetErr && <div style={{ padding: '12px', background: '#fef2f2', color: '#991b1b', borderRadius: '10px', marginBottom: '16px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px' }}><AlertCircle size={16} /> {resetErr}</div>}
        {resetMsg && <div style={{ padding: '12px', background: '#ecfdf5', color: '#0f766e', borderRadius: '10px', marginBottom: '16px', border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px' }}><CheckCircle2 size={16} /> {resetMsg}</div>}

        <form onSubmit={handleResetAccess} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '240px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>User Email Address</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Mail size={16} style={{ position: 'absolute', left: '13px', color: '#94a3b8', zIndex: 1 }} />
              <input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} onFocus={onFocus} onBlur={onBlur} placeholder="user@company.com" style={inputStyle} />
            </div>
          </div>
          <button type="submit" disabled={resetBusy || !resetEmail} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: resetBusy || !resetEmail ? '#cbd5e1' : 'linear-gradient(135deg, #6366f1, #7c3aed)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: resetBusy || !resetEmail ? 'not-allowed' : 'pointer', boxShadow: resetBusy || !resetEmail ? 'none' : '0 6px 16px rgba(99,102,241,.28)' }}>
            {resetBusy ? <span className="btn-spinner animate-spin" style={{ borderTopColor: 'white' }} /> : <Shield size={16} />}
            {resetBusy ? "Working..." : "Reset Access"}
          </button>
        </form>
      </div>
      </>
      )}

      {view === 'usage' && (
        <div className="animate-fade">
          {/* Data Health — catalog ↔ S3 consistency */}
          {health && (
            <div style={{ background: '#fff', border: `1px solid ${health.ok ? '#dcfce7' : '#fee2e2'}`, borderRadius: '12px', padding: '14px 18px', marginBottom: '16px', boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: health.ok ? 0 : '10px' }}>
                {health.ok
                  ? <CheckCircle2 size={16} color="#16a34a" />
                  : <AlertCircle size={16} color="#dc2626" />}
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Data Health</div>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                  {health.ok ? 'All clear — every active file has a schema, no phantom tables.' : `${health.findings.length} item(s) need attention`}
                </span>
              </div>
              {!health.ok && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {health.findings.map((f, i) => {
                    const color = f.type === 'orphan_folder' ? '#dc2626' : f.type === 'fallback_files' ? '#d97706' : '#64748b';
                    const label = f.type === 'orphan_folder' ? 'No schema' : f.type === 'fallback_files' ? 'Fallback' : 'Phantom';
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12.5px', color: '#475569', padding: '6px 0', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9' }}>
                        <span style={{ flexShrink: 0, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color, background: `${color}15`, padding: '2px 7px', borderRadius: '999px' }}>{label}</span>
                        <span style={{ flex: 1 }}><strong>{f.customerId}</strong> · {f.detail}</span>
                        {f.lastModified && (
                          <span style={{ flexShrink: 0, color: '#94a3b8', fontSize: '11.5px', whiteSpace: 'nowrap' }}
                            title={`File recorded: ${new Date(f.lastModified).toLocaleString()}`}>
                            {new Date(f.lastModified).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} · {timeAgo(f.lastModified)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>No schema = invisible to AI · Fallback = needs a real category · Phantom = schema with no files.</div>
                </div>
              )}
            </div>
          )}

          {/* Usage tracking toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1px solid #eaeef4', borderRadius: '12px', padding: '14px 18px', marginBottom: '16px', boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
            <div style={{ paddingRight: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Usage tracking {trackingEnabled === null ? '' : trackingEnabled ? '· On' : '· Off'}</div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Records content-agnostic events (no question text or data) to learn how the tool is used. Powers "last active" and the weekly summary.</div>
            </div>
            <button onClick={toggleTracking} disabled={togglingTracking || trackingEnabled === null} style={{ position: 'relative', flexShrink: 0, width: '46px', height: '26px', borderRadius: '999px', border: 'none', cursor: (togglingTracking || trackingEnabled === null) ? 'wait' : 'pointer', background: trackingEnabled ? '#16a34a' : '#cbd5e1', transition: 'background .2s' }} title={trackingEnabled ? 'On' : 'Off'}>
              <span style={{ position: 'absolute', top: '3px', left: trackingEnabled ? '23px' : '3px', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
            </button>
          </div>

          {/* Deployed backend version (from GET /admin/settings) */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1px solid #eaeef4', borderRadius: '12px', padding: '14px 18px', marginBottom: '16px', boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
            <div style={{ paddingRight: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Backend version</div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>The deployed API/Lambda version. Backend-only releases add a 4th segment (e.g. 1.9.17.1) and don't change the app version above.</div>
            </div>
            <span style={{ flexShrink: 0, fontSize: '13px', fontWeight: 700, color: '#334155', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 10px' }}>
              {backendVersion ? `v${backendVersion}` : '—'}
            </span>
          </div>

          {/* Totals */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Customers', value: usage?.totals?.customers ?? '—', icon: <Users size={16} />, color: '#6366f1' },
              { label: 'Active files', value: usage?.totals?.activeFiles ?? '—', icon: <BarChart3 size={16} />, color: '#0891b2' },
              { label: 'Storage', value: usage ? `${usage.totals.storageMb} MB` : '—', icon: <HardDrive size={16} />, color: '#7c3aed' },
              { label: `Questions (${usage?.month || 'mo'})`, value: usage?.totals?.questions ?? '—', icon: <MessageSquare size={16} />, color: '#16a34a' },
              { label: 'AI cost (mo)', value: usage ? `$${usage.totals.aiCost}` : '—', icon: <MessageSquare size={16} />, color: '#d97706' },
            ].map((s, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: s.color, marginBottom: '6px' }}>{s.icon}<span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: '#94a3b8' }}>{s.label}</span></div>
                <div style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a', letterSpacing: '-.5px' }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '14px', boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #eaeef4' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Per-customer usage</h2>
              <button onClick={fetchUsage} disabled={usageLoading} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>
                {usageLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
              </button>
            </div>

            {usageError && <div style={{ padding: '16px 20px', color: '#991b1b', fontSize: '13.5px' }}>{usageError}</div>}

            {usageLoading && !usage ? (
              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[0, 1, 2].map(i => <div key={i} className="skeleton" style={{ height: '18px', width: `${90 - i * 12}%` }} />)}
              </div>
            ) : usage && usage.customers.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', textAlign: 'left', color: '#475569' }}>
                      <th style={{ padding: '10px 16px', fontWeight: 600 }}>Customer</th>
                      <th style={{ padding: '10px 12px', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '10px 12px', fontWeight: 600, textAlign: 'right' }}>Files</th>
                      <th style={{ padding: '10px 12px', fontWeight: 600, textAlign: 'right' }}>Cat.</th>
                      <th style={{ padding: '10px 12px', fontWeight: 600, textAlign: 'right' }}>Storage</th>
                      <th style={{ padding: '10px 12px', fontWeight: 600, textAlign: 'right' }}>Questions</th>
                      <th style={{ padding: '10px 12px', fontWeight: 600 }}>Last active</th>
                      <th style={{ padding: '10px 16px', fontWeight: 600 }}>Budget used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...usage.customers]
                      .sort((a, b) => {
                        // Most recently active first; never-active ("—") sink to the bottom.
                        const ta = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
                        const tb = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
                        return tb - ta;
                      })
                      .map((c) => {
                      const pct = Math.min(c.budgetUsedPct || 0, 100);
                      const barColor = pct >= 90 ? '#dc2626' : pct >= 70 ? '#d97706' : '#16a34a';
                      return (
                      <tr key={c.customerId} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <span style={{ fontWeight: 600, color: '#0f172a' }}>{c.companyName || c.customerId}</span>
                            {c.role === 'admin' && <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '999px', background: '#eef2ff', color: '#4f46e5' }}>admin</span>}
                          </div>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>{c.email || c.customerId}</div>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          {(() => { const p = presence(c.lastActiveAt, c.isActive); return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', fontWeight: 600, color: p.color }} title={`Last active: ${timeAgo(c.lastActiveAt)}`}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: p.dot }} />
                            {p.label}
                          </span> ); })()}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#0f172a' }}>{c.activeFiles}{c.archivedFiles > 0 && <span style={{ color: '#cbd5e1' }}> +{c.archivedFiles}</span>}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#0f172a' }}>{c.categories}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#0f172a' }}>{c.storageMb} MB</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#0f172a' }}>{c.questionsThisMonth}</td>
                        <td style={{ padding: '10px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>{timeAgo(c.lastActiveAt)}</td>
                        <td style={{ padding: '10px 16px', minWidth: '150px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1, height: '7px', background: '#f1f5f9', borderRadius: '999px', overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '999px' }} />
                            </div>
                            <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>${c.aiCostThisMonth} / ${c.monthlyBudget}</span>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '13.5px' }}>No customers yet.</div>
            )}
          </div>
          {/* Tool Health (7-day) — failure modes + recent failures */}
          <div style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '14px', boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06)', marginTop: '16px', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eaeef4' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Tool Health — last 7 days</h2>
              <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#94a3b8' }}>Failure modes and recent failures (generated SQL + engine error — no question text unless a user flagged an answer).</p>
            </div>
            <div style={{ padding: '16px 20px' }}>
              {insights && insights.enabled ? (
                <>
                  {Object.keys(insights.failureModes || {}).length > 0 ? (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                      {Object.entries(insights.failureModes).sort((a, b) => b[1] - a[1]).map(([mode, count]) => (
                        <span key={mode} style={{ fontSize: '12px', fontWeight: 600, padding: '5px 12px', borderRadius: '999px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>{mode}: {count}</span>
                      ))}
                    </div>
                  ) : <p style={{ fontSize: '13px', color: '#16a34a', margin: '0 0 12px' }}>No failures recorded in the last 7 days. 🎉</p>}
                  {(insights.recentFailures || []).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {insights.recentFailures.map((f, idx) => (
                        <details key={idx} style={{ border: '1px solid #f1f5f9', borderRadius: '8px', padding: '8px 12px' }}>
                          <summary style={{ cursor: 'pointer', fontSize: '12.5px', color: '#334155', display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', fontWeight: 700, padding: '1px 7px', borderRadius: '999px', background: f.type === 'answer_feedback' ? '#eef2ff' : '#fef2f2', color: f.type === 'answer_feedback' ? '#4f46e5' : '#b91c1c' }}>{f.type === 'answer_feedback' ? 'flagged' : f.mode}</span>
                            <span style={{ color: '#94a3b8', fontSize: '11px' }}>{f.ts ? f.ts.replace('T', ' ').slice(0, 16) : ''}</span>
                          </summary>
                          <div style={{ marginTop: '8px', fontSize: '11.5px', color: '#475569' }}>
                            {f.question && <div style={{ marginBottom: '6px' }}><strong>Question:</strong> {f.question}</div>}
                            {f.error && <div style={{ marginBottom: '6px', color: '#b91c1c' }}><strong>Error:</strong> {f.error}</div>}
                            {f.sql && <pre style={{ margin: 0, padding: '8px', background: '#0f172a', color: '#e2e8f0', borderRadius: '6px', fontSize: '11px', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>{f.sql}</pre>}
                          </div>
                        </details>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Turn on usage tracking above to start collecting tool-health data.</p>
              )}
            </div>
          </div>

          <p style={{ margin: '10px 4px 0', fontSize: '11.5px', color: '#94a3b8' }}>Questions &amp; AI cost reset monthly. Storage includes raw backups and archived files.</p>
        </div>
      )}

      {view === 'features' && (
        <div className="animate-fade">
          <div style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '12px', padding: '14px 18px', marginBottom: '16px', boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ paddingRight: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Feature flags</div>
                <div style={{ fontSize: '12px', color: '#94a3b8' }}>Ship code to production without exposing it. Turn a feature on globally, or enable it for just one tenant (e.g. your test account) first, then roll out. Per-tenant overrides win over the global value.</div>
              </div>
              <button onClick={fetchFlags} disabled={flagsLoading} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                {flagsLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
              </button>
            </div>
          </div>

          {flagsLoading && !flags ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[0, 1].map(i => <div key={i} className="skeleton" style={{ height: '90px', borderRadius: '14px' }} />)}
            </div>
          ) : flags && flags.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {flags.map((f) => {
                const globalEffective = f.global !== null ? f.global : f.default;
                const busyGlobal = flagBusy === `global:${f.key}:`;
                return (
                  <div key={f.key} style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '14px', boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06)', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px', borderBottom: flagTenants.length ? '1px solid #eaeef4' : 'none' }}>
                      <div style={{ paddingRight: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{f.label}</span>
                          <code style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '1px 7px', borderRadius: '6px' }}>{f.key}</code>
                          {f.global === null && <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '999px', background: '#f1f5f9', color: '#94a3b8' }}>default {f.default ? 'on' : 'off'}</span>}
                        </div>
                        <div style={{ fontSize: '12.5px', color: '#94a3b8', marginTop: '4px', maxWidth: '620px' }}>{f.description}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: '#94a3b8' }}>Global</span>
                        <button onClick={() => setGlobalFlag(f.key, !globalEffective)} disabled={busyGlobal} style={{ position: 'relative', width: '46px', height: '26px', borderRadius: '999px', border: 'none', cursor: busyGlobal ? 'wait' : 'pointer', background: globalEffective ? '#16a34a' : '#cbd5e1', transition: 'background .2s' }} title={globalEffective ? 'On' : 'Off'}>
                          <span style={{ position: 'absolute', top: '3px', left: globalEffective ? '23px' : '3px', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
                        </button>
                      </div>
                    </div>

                    {flagTenants.length > 0 && (
                      <div style={{ padding: '12px 20px 16px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: '#94a3b8', marginBottom: '10px' }}>Per-tenant overrides</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {flagTenants.map((t) => {
                            const ov = t.overrides ? t.overrides[f.key] : undefined;  // true | false | undefined(inherit)
                            const busyT = flagBusy === `tenant:${f.key}:${t.customerId}`;
                            const opts = [
                              { label: 'Inherit', val: null, active: ov === undefined },
                              { label: 'On', val: true, active: ov === true },
                              { label: 'Off', val: false, active: ov === false },
                            ];
                            return (
                              <div key={t.customerId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                <div style={{ minWidth: 0 }}>
                                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{t.companyName || t.customerId}</span>
                                  <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '8px' }}>{t.customerId}</span>
                                </div>
                                <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', borderRadius: '8px', padding: '3px', flexShrink: 0 }}>
                                  {busyT ? <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px' }}><Loader2 size={14} className="animate-spin" color="#94a3b8" /></span> : opts.map(o => (
                                    <button key={o.label} onClick={() => setTenantFlag(f.key, t.customerId, o.val)} disabled={o.active} style={{
                                      padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, border: 'none',
                                      cursor: o.active ? 'default' : 'pointer',
                                      background: o.active ? (o.val === true ? '#16a34a' : o.val === false ? '#dc2626' : '#fff') : 'transparent',
                                      color: o.active ? (o.val === null ? '#0f172a' : '#fff') : '#64748b',
                                      boxShadow: o.active && o.val === null ? '0 1px 2px rgba(16,24,40,.1)' : 'none',
                                    }}>{o.label}</button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '14px', padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '13.5px' }}>No feature flags registered.</div>
          )}
        </div>
      )}

      {view === 'models' && (
        <div className="animate-fade">
          {/* Step-by-step reminder — how to switch the LLM safely */}
          <div style={{ background: 'linear-gradient(135deg,#eef2ff,#faf5ff)', border: '1px solid #e0e7ff', borderRadius: '12px', padding: '16px 18px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <Cpu size={16} color="#4f46e5" />
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Switching the LLM — do these in order</div>
            </div>
            <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: 1.7, color: '#475569' }}>
              <li><strong>Trial on the test account first</strong> (recommended) — use "Trial a candidate model on one tenant" below. No redeploy; only that tenant uses the new model.</li>
              <li><strong>Validate</strong> — ask your usual questions as that tenant, then check <strong>Usage → Tool Health</strong> for new failures.</li>
              <li><strong>Switch everyone</strong> — pick the model in "Global default model" below and Save. Applies immediately to all tenants, <strong>no redeploy</strong>.</li>
              <li><strong>Clean up</strong> — clear the trial override so that tenant inherits the new global default.</li>
            </ol>
            <div style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '10px' }}>The app doesn't auto-test a new model — your validation (step 2) is the check. Switching (global or per-tenant) is a live config change here; no code edit or deploy needed. For a deeper pre-flight, the terminal eval harness (<code style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '5px', padding: '1px 5px', fontSize: '11.5px' }}>backend/eval/run_eval.py</code>) still exists but is optional.</div>
          </div>

          {modelsLoading && !models ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', fontSize: '13.5px', padding: '12px' }}><Loader2 size={16} className="animate-spin" /> Loading models…</div>
          ) : (
            <>
              {modelError && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: '12px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', fontWeight: 600 }}>
                  <span style={{ fontSize: '15px', lineHeight: '18px' }}>⚠️</span>
                  <div style={{ flex: 1 }}>{modelError}</div>
                  <button onClick={() => setModelError("")} style={{ border: 'none', background: 'none', color: '#b91c1c', cursor: 'pointer', fontWeight: 700 }}>✕</button>
                </div>
              )}
              {/* Global default model — the live switcher (no redeploy) */}
              <div style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '12px', padding: '16px 18px', marginBottom: '16px', boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>Global default model</div>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '12px' }}>Applies to every tenant that has no per-tenant override. Saving takes effect immediately — <strong>no redeploy</strong>.{models?.available?.length ? '' : ' (Could not list available models — you can still type an id.)'}</div>
                {['smart', 'fast'].map(tier => {
                  const isOverride = !!models?.platformOverrides?.[tier];
                  const envDefault = models?.envDefaults?.[tier];
                  const avail = models?.available || [];
                  const cur = models?.defaults?.[tier];
                  const opts = avail.includes(cur) ? avail : (cur ? [cur, ...avail] : avail);
                  return (
                    <div key={tier} style={{ padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: tier === 'smart' ? '#7c3aed' : '#0891b2', width: '46px' }}>{tier === 'smart' ? 'SMART' : 'FAST'}</span>
                        <select value={defaultSel[tier] || ''} onChange={e => setDefaultSel(s => ({ ...s, [tier]: e.target.value }))} disabled={modelBusy}
                          style={{ ...inputStyle, paddingLeft: '10px', flex: '1 1 320px', cursor: 'pointer', fontFamily: 'ui-monospace,Menlo,monospace', fontSize: '12px' }} onFocus={onFocus} onBlur={onBlur}>
                          {opts.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <button disabled={modelBusy || !defaultSel[tier] || defaultSel[tier] === cur}
                          onClick={() => setModelDefault(tier, defaultSel[tier])}
                          style={{ padding: '8px 14px', borderRadius: '9px', border: 'none', background: (modelBusy || !defaultSel[tier] || defaultSel[tier] === cur) ? '#c7d2fe' : '#4f46e5', color: '#fff', fontWeight: 700, fontSize: '13px', cursor: (modelBusy || defaultSel[tier] === cur) ? 'default' : 'pointer' }}>Save</button>
                        {isOverride && (
                          <button disabled={modelBusy} onClick={() => setModelDefault(tier, null)}
                            style={{ padding: '8px 12px', borderRadius: '9px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontWeight: 600, fontSize: '12.5px', cursor: 'pointer' }}>Reset to build default</button>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', paddingLeft: '56px', fontSize: '11px', color: '#94a3b8', flexWrap: 'wrap' }}>
                        <span>live: <code style={{ color: '#0f172a' }}>{cur || '—'}</code></span>
                        {models?.pricing?.[tier] && <span>· ${models.pricing[tier].in}/${models.pricing[tier].out} per 1M</span>}
                        {isOverride
                          ? <span style={{ color: '#7c3aed', fontWeight: 700 }}>· UI override (build default: {envDefault})</span>
                          : <span>· using build default</span>}
                      </div>
                    </div>
                  );
                })}
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '10px' }}>All calls go through the provider-agnostic Converse adapter, so a tier can point at any listed model — even a non-Claude provider. SMART = AI-Insights reasoning · FAST = classification/interpretation/diagnosis. See the table below for the full per-activity mapping.</div>
              </div>

              {/* Which model each activity uses (declarative catalog from the backend) */}
              {models?.activities?.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '12px', padding: '16px 18px', marginBottom: '16px', boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>Which model each activity uses</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '12px' }}>Each activity runs on its tier's model. "Benefit" = how much a smarter model helps that task.</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                        <th style={{ padding: '6px 8px 6px 0' }}>Activity</th>
                        <th style={{ padding: '6px 8px' }}>Tier</th>
                        <th style={{ padding: '6px 8px' }}>Model</th>
                        <th style={{ padding: '6px 0 6px 8px' }}>Smart-model benefit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {models.activities.map(a => (
                        <tr key={a.key} style={{ borderTop: '1px solid #f1f5f9' }} title={a.description}>
                          <td style={{ padding: '7px 8px 7px 0', color: '#0f172a', fontWeight: 600 }}>{a.label}</td>
                          <td style={{ padding: '7px 8px' }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: a.tier === 'smart' ? '#7c3aed' : '#0891b2', background: a.tier === 'smart' ? '#f5f3ff' : '#ecfeff', border: `1px solid ${a.tier === 'smart' ? '#e9d5ff' : '#a5f3fc'}`, borderRadius: '6px', padding: '2px 7px' }}>{a.tier === 'smart' ? 'SMART' : 'FAST'}</span>
                          </td>
                          <td style={{ padding: '7px 8px' }}><code style={{ fontSize: '11.5px', color: '#475569' }}>{a.model_id}</code></td>
                          <td style={{ padding: '7px 0 7px 8px', color: a.benefit === 'high' ? '#dc2626' : a.benefit === 'medium' ? '#b45309' : '#94a3b8', fontWeight: a.benefit === 'high' ? 700 : 600 }}>{a.benefit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Start a per-tenant trial */}
              <div style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '12px', padding: '16px 18px', marginBottom: '16px', boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>Trial a candidate model on one tenant</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 200px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' }}>Tenant (customer_id)</label>
                    <input value={trialCustomer} onChange={e => setTrialCustomer(e.target.value)} placeholder="testuser_customer" style={{ ...inputStyle, paddingLeft: '12px' }} onFocus={onFocus} onBlur={onBlur} />
                  </div>
                  <div style={{ flex: '0 0 110px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' }}>Tier</label>
                    <select value={trialTier} onChange={e => setTrialTier(e.target.value)} style={{ ...inputStyle, paddingLeft: '12px', cursor: 'pointer' }} onFocus={onFocus} onBlur={onBlur}>
                      <option value="smart">smart</option>
                      <option value="fast">fast</option>
                    </select>
                  </div>
                  <div style={{ flex: '2 1 260px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' }}>Candidate model ID</label>
                    <input value={trialModelId} onChange={e => setTrialModelId(e.target.value)} list="availModelsList" placeholder="pick or type — us.anthropic.claude-…" style={{ ...inputStyle, paddingLeft: '12px' }} onFocus={onFocus} onBlur={onBlur} />
                    <datalist id="availModelsList">{(models?.available || []).map(m => <option key={m} value={m} />)}</datalist>
                  </div>
                  <button
                    onClick={async () => { if (trialCustomer.trim() && trialModelId.trim()) { const ok = await setModelOverride(trialCustomer.trim(), trialTier, trialModelId.trim()); if (ok) setTrialModelId(""); } }}
                    disabled={modelBusy || !trialCustomer.trim() || !trialModelId.trim()}
                    style={{ flex: '0 0 auto', padding: '11px 18px', borderRadius: '10px', border: 'none', background: (modelBusy || !trialCustomer.trim() || !trialModelId.trim()) ? '#cbd5e1' : '#4f46e5', color: '#fff', fontSize: '13.5px', fontWeight: 600, cursor: (modelBusy || !trialCustomer.trim() || !trialModelId.trim()) ? 'not-allowed' : 'pointer' }}>
                    {modelBusy ? <Loader2 size={14} className="animate-spin" /> : 'Start trial'}
                  </button>
                </div>
              </div>

              {/* Active overrides */}
              <div style={{ background: '#fff', border: '1px solid #eaeef4', borderRadius: '12px', padding: '16px 18px', boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>Active per-tenant overrides</div>
                {(models?.tenantOverrides || []).length === 0 ? (
                  <div style={{ fontSize: '13px', color: '#94a3b8', paddingTop: '8px' }}>None — every tenant is using the global defaults above.</div>
                ) : (
                  models.tenantOverrides.map(t => (
                    Object.entries(t.overrides).map(([tier, mid]) => (
                      <div key={t.customerId + tier} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{t.customerId} <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: tier === 'smart' ? '#7c3aed' : '#0891b2', marginLeft: '4px' }}>{tier}</span></div>
                          <code style={{ fontSize: '12px', color: '#64748b' }}>{mid}</code>
                        </div>
                        <button onClick={() => setModelOverride(t.customerId, tier, null)} disabled={modelBusy} style={{ flex: '0 0 auto', padding: '6px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#dc2626', fontSize: '12.5px', fontWeight: 600, cursor: modelBusy ? 'wait' : 'pointer' }}>Clear</button>
                      </div>
                    ))
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}